// Cave84 — PivotView (F8.3)
// Region × Wine-Style matrix. Rows = appellations/regions, Cols = wine styles.
// Each cell shows bottle count and portfolio value (EUR).
// Clicking a cell fires onCellClick(region, style) which filters the main inventory.

import { useMemo } from 'react';
import type { WineStyle } from '@cave84/shared-types';
import type { GroupedInventoryRow } from '@/hooks/useInventory';
import { Money } from '@/components/Money';

// ─── types ───────────────────────────────────────────────────────────────────

interface PivotCell {
  count: number;
  value: number; // EUR
}

interface PivotRow {
  region: string;
  cells: Map<WineStyle, PivotCell>;
  total: PivotCell;
}

// ─── constants ───────────────────────────────────────────────────────────────

const STYLES: WineStyle[] = ['red', 'white', 'sparkling', 'rose', 'fortified', 'sweet', 'orange'];

const STYLE_LABEL: Record<WineStyle, string> = {
  red: 'Red', white: 'White', sparkling: 'Sparkling', rose: 'Rosé',
  fortified: 'Fortified', sweet: 'Sweet', orange: 'Orange',
};

const STYLE_DOT: Record<WineStyle, string> = {
  red: '#C49255', white: '#D4B896', rose: '#E8829A', sparkling: '#93C5D9',
  fortified: '#C47A2A', sweet: '#E5CE6A', orange: '#D4843A',
};

// Cell heat-map: intensity based on bottle count relative to max in matrix.
function heatAlpha(count: number, max: number): number {
  if (!max || !count) return 0;
  return 0.04 + (count / max) * 0.14;
}

// ─── component ───────────────────────────────────────────────────────────────

export function PivotView({
  rows,
  onCellClick,
}: {
  rows: GroupedInventoryRow[];
  onCellClick: (region: string, style: WineStyle) => void;
}) {
  // ── build pivot matrix ───────────────────────────────────────────────────
  const { pivotRows, colTotals, grandTotal, maxCellCount } = useMemo(() => {
    const regionMap = new Map<string, Map<WineStyle, PivotCell>>();

    for (const row of rows) {
      const region = row.vintage?.drinking_window_current?.from_year != null
        ? (row.appellation?.region ?? row.producer.name)
        : (row.appellation?.region ?? row.producer.name);

      const style  = row.wine.style as WineStyle;
      const count  = row.qty;
      const value  = (row.marketPrice ?? 0) * count;

      if (!regionMap.has(region)) regionMap.set(region, new Map());
      const styleMap = regionMap.get(region)!;
      const existing = styleMap.get(style) ?? { count: 0, value: 0 };
      styleMap.set(style, { count: existing.count + count, value: existing.value + value });
    }

    // Build sorted pivot rows (by total bottles desc)
    const pivotRows: PivotRow[] = Array.from(regionMap.entries())
      .map(([region, cells]) => {
        let totalCount = 0, totalValue = 0;
        for (const cell of cells.values()) { totalCount += cell.count; totalValue += cell.value; }
        return { region, cells, total: { count: totalCount, value: totalValue } };
      })
      .sort((a, b) => b.total.count - a.total.count);

    // Column totals
    const colTotals = new Map<WineStyle, PivotCell>();
    for (const style of STYLES) {
      let c = 0, v = 0;
      for (const pr of pivotRows) {
        const cell = pr.cells.get(style);
        if (cell) { c += cell.count; v += cell.value; }
      }
      if (c) colTotals.set(style, { count: c, value: v });
    }

    // Grand total
    const grandTotal = pivotRows.reduce(
      (acc, r) => ({ count: acc.count + r.total.count, value: acc.value + r.total.value }),
      { count: 0, value: 0 }
    );

    // Max cell count for heat intensity
    let maxCellCount = 0;
    for (const pr of pivotRows) {
      for (const cell of pr.cells.values()) {
        if (cell.count > maxCellCount) maxCellCount = cell.count;
      }
    }

    return { pivotRows, colTotals, grandTotal, maxCellCount };
  }, [rows]);

  // Only show styles that appear in data
  const activeStyles = STYLES.filter(s => colTotals.has(s));

  if (pivotRows.length === 0) {
    return (
      <div style={{ padding:'64px 20px', textAlign:'center', color:'#908977', fontSize:14 }}>
        No data to pivot — adjust your filters.
      </div>
    );
  }

  const TH: React.CSSProperties = {
    padding:'12px 16px', fontSize:11, textTransform:'uppercase', letterSpacing:'0.14em',
    color:'#9C9483', fontWeight:600, textAlign:'center', whiteSpace:'nowrap',
    borderBottom:'1px solid #2F3645', background:'#181D26',
  };

  return (
    <div style={{ overflowX:'auto' }}>
      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12, tableLayout:'auto' }}>
        <thead>
          <tr>
            {/* Region label col */}
            <th style={{ ...TH, textAlign:'left', width:180, position:'sticky', left:0, background:'#181D26', zIndex:2 }}>
              Region
            </th>
            {activeStyles.map(s => (
              <th key={s} style={TH}>
                <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:4 }}>
                  <span style={{ width:7, height:7, borderRadius:'50%', background:STYLE_DOT[s], display:'block' }}/>
                  {STYLE_LABEL[s]}
                </div>
              </th>
            ))}
            <th style={{ ...TH, color:'#B3AC97' }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {pivotRows.map((pr, ri) => (
            <tr key={pr.region} style={{ borderBottom:'1px solid #232936' }}>
              {/* Region name */}
              <td style={{ padding:'12px 16px', fontWeight:500, color:'#ECE6D4', fontSize:13, position:'sticky', left:0, background:ri%2===0?'#0E1116':'#1C1915', zIndex:1, whiteSpace:'nowrap', maxWidth:180, overflow:'hidden', textOverflow:'ellipsis' }}>
                {pr.region}
              </td>

              {activeStyles.map(s => {
                const cell = pr.cells.get(s);
                if (!cell) {
                  return (
                    <td key={s} style={{ padding:'12px 16px', textAlign:'center', color:'#2F3645' }}>
                      —
                    </td>
                  );
                }
                const alpha = heatAlpha(cell.count, maxCellCount);
                return (
                  <td
                    key={s}
                    onClick={() => onCellClick(pr.region, s)}
                    style={{ padding:'10px 12px', textAlign:'center', cursor:'pointer', background:`rgba(196,146,85,${alpha})`, transition:'background 0.15s', verticalAlign:'middle' }}
                    onMouseEnter={e => { (e.currentTarget as HTMLTableCellElement).style.background=`rgba(196,146,85,${alpha+0.08})`; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLTableCellElement).style.background=`rgba(196,146,85,${alpha})`; }}
                  >
                    <div style={{ fontFamily:"'Fraunces',serif", fontSize:18, fontWeight:500, color:'#ECE6D4', lineHeight:1 }}>{cell.count}</div>
                    {cell.value > 0 && (
                      <div style={{ fontSize:11, color:'#9C9483', marginTop:3, fontFamily:"'JetBrains Mono',monospace" }}>
                        <Money value={cell.value} currency="EUR" minWidthCh={6} forceVisible />
                      </div>
                    )}
                  </td>
                );
              })}

              {/* Row total */}
              <td style={{ padding:'12px 16px', textAlign:'center', borderLeft:'1px solid #232936' }}>
                <div style={{ fontFamily:"'Fraunces',serif", fontSize:16, fontWeight:500, color:'#C49255', lineHeight:1 }}>{pr.total.count}</div>
                {pr.total.value > 0 && (
                  <div style={{ fontSize:11, color:'#908977', marginTop:3, fontFamily:"'JetBrains Mono',monospace" }}>
                    <Money value={pr.total.value} currency="EUR" minWidthCh={6} forceVisible />
                  </div>
                )}
              </td>
            </tr>
          ))}

          {/* Column totals row */}
          <tr style={{ borderTop:'2px solid #2F3645', background:'#181D26' }}>
            <td style={{ padding:'12px 16px', fontSize:11, textTransform:'uppercase', letterSpacing:'0.12em', color:'#9C9483', fontWeight:600, position:'sticky', left:0, background:'#181D26', zIndex:1 }}>
              Total
            </td>
            {activeStyles.map(s => {
              const ct = colTotals.get(s);
              return (
                <td key={s} style={{ padding:'12px 16px', textAlign:'center', borderTop:'1px solid #2F3645' }}>
                  {ct ? (
                    <>
                      <div style={{ fontFamily:"'Fraunces',serif", fontSize:15, fontWeight:500, color:'#B3AC97' }}>{ct.count}</div>
                      {ct.value > 0 && (
                        <div style={{ fontSize:11, color:'#908977', marginTop:2, fontFamily:"'JetBrains Mono',monospace" }}>
                          <Money value={ct.value} currency="EUR" minWidthCh={6} forceVisible />
                        </div>
                      )}
                    </>
                  ) : <span style={{ color:'#2F3645' }}>—</span>}
                </td>
              );
            })}
            <td style={{ padding:'12px 16px', textAlign:'center', borderLeft:'1px solid #2F3645', borderTop:'1px solid #2F3645' }}>
              <div style={{ fontFamily:"'Fraunces',serif", fontSize:18, fontWeight:600, color:'#C49255' }}>{grandTotal.count}</div>
              {grandTotal.value > 0 && (
                <div style={{ fontSize:11, color:'#9C9483', marginTop:3, fontFamily:"'JetBrains Mono',monospace" }}>
                  <Money value={grandTotal.value} currency="EUR" minWidthCh={6} forceVisible />
                </div>
              )}
            </td>
          </tr>
        </tbody>
      </table>

      <div style={{ padding:'12px 16px', fontSize:11, color:'#908977', borderTop:'1px solid #232936' }}>
        Click any cell to filter inventory to that region + style.
      </div>
    </div>
  );
}
